import { useFormikContext } from 'formik'
import { reverse } from 'named-urls'
import React from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { useDashboardPermissions } from '@components'
import { routing } from '@utils/routing'

import { useAdminModeContext } from '../../../../../App/providers/AdminModeProvider'
import { useOffer } from '../../../hooks/useOffer'
import type { ProductFormState, VariantType } from '../../SingleProduct.types'
import { useProductPricing } from '../../hooks/useProductPricing'
import { VariantsView } from './VariantsView'
import { useStock } from './hooks/useStock'
import { useVariants } from './hooks/useVariants'
import type { VariantsContainerProps, VariantsViewProps } from './types'
import { updateValuesVariantQuantity } from './utils'

export const VariantsContainer = ({ applyToAllVariants }: VariantsContainerProps) => {
  const adminMode = useAdminModeContext()
  const location = useLocation()
  const navigate = useNavigate()
  const { productId = '' } = useParams<'productId'>()
  const { hasPermission } = useDashboardPermissions()
  const { offer } = useOffer(productId)
  const { showPricingPerVariant } = useProductPricing()

  const { values, initialValues, setValues, resetForm } = useFormikContext<ProductFormState>()
  const { variants } = useVariants(values.variants, productId)

  const onStockChangeSuccess = ({ id, quantity }: { id: string; quantity: number | undefined }) => {
    // NOTE: Update initialValues to avoid confirm bar showing up
    resetForm({ values: updateValuesVariantQuantity(initialValues, id, quantity) })
    // NOTE: Update Values to keep current changes after resetting the form
    setValues(updateValuesVariantQuantity(values, id, quantity))
  }

  const { handleStockChange } = useStock({ productId, onSuccess: onStockChangeSuccess })

  const showVariantsWeight = offer.variantTypes.some(
    (variantType: VariantType) => variantType.variesBy.weight,
  )

  const showVariantsTable =
    (offer.offerType === 'MANUAL' ||
      // NOTE: Show table to OVERCAST manufacturing service products - CORE-5228
      (offer.offerType === 'MOQ' && offer.manufacturingService === 'OVERCAST') ||
      showPricingPerVariant ||
      !!adminMode?.isActive) &&
    variants.length >= 2
  const viewConfig = {
    showVariantsTable,
    showPricing: showPricingPerVariant,
    showWeight: offer.offerType !== 'DIGITAL',
    showVariantsWeight,
    showManualStockNotice:
      (offer.offerType === 'ON_DEMAND' || offer.offerType === 'MOQ') && !adminMode?.isActive,
    showExternalStock: ['SHIP_MONK', 'WHIPLASH_3PL', 'WHIPLASH_FREE', 'CART_JAZZ'].includes(
      offer.fulfillmentService,
    ),
    showSingleSkuInput: !showVariantsTable && values.visualHints.customSkus,
    editable: hasPermission('products.edit.details'),
    variantsEditable: offer.offerType === 'MANUAL',
    stockEditable:
      adminMode?.isActive ||
      (hasPermission('products.edit.stock') && ['MANUAL', 'DIGITAL'].includes(offer.offerType)),
  }

  const handleVariantTypeAdd = () => {
    navigate(`${reverse(routing.products.all.product.variants, { productId })}?${location.search}`)
  }

  const handleIsCompareAtPriceEnabledValueChange: VariantsViewProps['onIsCompareAtPriceEnabledValueChange'] =
    (value) => {
      if (!value) {
        applyToAllVariants({ compareAtPriceValue: null })
      }
    }

  return (
    <VariantsView
      data={variants}
      membershipTiers={offer.memberships.tiers}
      viewConfig={viewConfig}
      onVariantTypeAdd={handleVariantTypeAdd}
      onStockChange={handleStockChange}
      onIsCompareAtPriceEnabledValueChange={handleIsCompareAtPriceEnabledValueChange}
    />
  )
}
